import { type ReactNode } from 'react';

import { WhitePanel } from '../../../../../components/Toolkit/WhitePanel/WhitePanel';
import { LinkButton } from '../../../../../components/Toolkit/Button/LinkButton';
import { MiniUserProfile } from '../../../../../components/Toolkit/MiniUserProfile/MiniUserProfile';

import { VerifiedList } from '../../../../../components/PageWrappers/AdDetails/components/VerifiedList/VerifiedList';
import { Verifications } from '../../../../../components/PageWrappers/AdDetails/components/Verifications/Verifications';
import {
  SELLER_TYPES,
  type ReservationStatuses,
  type UserVerification,
  SellerTypes,
} from '../../types';
import type { UserRating } from '../../../../../api/types/adviewApiTypes';

import { StarRatingGroup } from '../../../../../components/PageWrappers/AdDetails/components/GoogleReviews/StarRatingGroup/StarRatingGroup';

import * as Styled from '../../../../../components/PageWrappers/AdDetails/components/SideSellerInfoPanel/SideSellerInfoPanel.styled';
import { VerifiedTitle } from '../../../../../components/PageWrappers/AdDetails/components/Verifications/VerifiedTitle/VerifiedTitle';
import getConfig from 'next/config';
import { SellerRatingsSummary } from './SideSellerInfoPanel.styled';
import { Spacer } from '../../../../../helpers/genericStyles';
import { ReserveCTA } from '../ReserveCTA/ReserveCTA';
import { ShieldCheckmark2Ic } from '@dsch/dd-icons';
import { useTheme } from 'styled-components';

export interface SideSellerInfoPanelProps {
  title?: string;
  renderSendMessage?: ReactNode;
  renderShowPhone?: ReactNode;
  renderMakeOffer?: ReactNode;
  county?: string;
  type?: SellerTypes;
  sellerUrl?: string;
  isTrader: boolean;
  verification?: UserVerification;
  className?: string;
  testId?: string;
  isDealer?: boolean;
  showroomUrl?: string;
  illustration?: string;
  isTrustedDealer?: boolean;
  franchiseType?: string;
  dealerReviews?: string;
  dealerRating?: string;
  dealerRatingCategory?: string;
  displayDealerReviews: boolean;
  userRating?: UserRating;
  membershipDuration?: string;
  dealerMembershipDuration?: string;
  onClickRatings?: () => void;
  onClickDealerRatings?: () => void;
  onClickReviews?: () => void;
  onClickDealerReviews?: () => void;
  onClickTitle?: () => void;
  onClickTrustedDealerType?: () => void;
  reservationStatus?: ReservationStatuses;
  isCtaVariantsFlag: boolean;
}

const SideSellerInfoPanel = (props: SideSellerInfoPanelProps) => {
  const theme = useTheme();
  const {
    publicRuntimeConfig: { FEATURE_TDP_UPDATES },
  } = getConfig();
  const {
    title,
    renderSendMessage,
    renderShowPhone,
    renderMakeOffer,
    county,
    type = SELLER_TYPES.PRIVATE,
    sellerUrl,
    isTrader,
    verification,
    className,
    testId,
    isDealer,
    showroomUrl,
    illustration,
    isTrustedDealer,
    franchiseType,
    dealerReviews,
    dealerRating,
    dealerRatingCategory,
    displayDealerReviews,
    userRating,
    membershipDuration,
    dealerMembershipDuration,
    onClickRatings,
    onClickDealerRatings,
    onClickReviews,
    onClickDealerReviews,
    onClickTitle,
    onClickTrustedDealerType,
    reservationStatus,
    isCtaVariantsFlag,
  } = props;

  const DISPLAY_USER_RATING = !isDealer;

  const viewAllAdsDisplayText = {
    [SELLER_TYPES.TRADER]: 'View our shop',
    [SELLER_TYPES.DEALER]: 'Visit showroom',
    [SELLER_TYPES.PRIVATE]: 'View all ads',
  };

  const renderVerificationComponent = (verification?: UserVerification) => {
    if (!verification || isDealer) return null;

    if (DISPLAY_USER_RATING) {
      return <Verifications verifications={verification} />;
    }

    return <VerifiedList verification={verification} />;
  };

  return (
    <WhitePanel className={className} testId={testId}>
      <Styled.MiniUserProfileAndVerifiedListContainer
        googleReviews={Boolean(dealerReviews)}
      >
        {title && (
          <MiniUserProfile
            title={
              DISPLAY_USER_RATING && verification ? (
                <VerifiedTitle
                  title={title}
                  verifications={verification}
                  onClickTitle={onClickTitle}
                />
              ) : (
                <LinkButton
                  ofType="SECONDARY"
                  fontSize="L18"
                  onClick={onClickTitle}
                >
                  {title}
                </LinkButton>
              )
            }
            illustration={illustration}
            county={county}
            type={type}
            isTrader={isTrader}
            verification={verification}
            isDealer={isDealer}
            position="SIDE"
          />
        )}

        <Spacer marginBottom="M16" />

        {renderVerificationComponent(verification)}
      </Styled.MiniUserProfileAndVerifiedListContainer>
      {displayDealerReviews && dealerReviews && !FEATURE_TDP_UPDATES && (
        <Styled.DealerRatings>
          {dealerRating && (
            <Styled.DealerReviewInfoTitle
              title={''}
              listItems={[
                <StarRatingGroup
                  key={1}
                  rating={parseFloat(dealerRating)}
                  totalReviews={dealerReviews}
                  clickTargetElementId="dealer-panel"
                  starWidth="20"
                  starHeight="20"
                  totalReviewsTrackingId={'google-reviews-total-reviews'}
                />,
              ]}
            />
          )}
        </Styled.DealerRatings>
      )}
      {FEATURE_TDP_UPDATES && isTrustedDealer && (
        <Styled.TrustedDealerTypeButton
          onClick={onClickTrustedDealerType}
          aria-label="Open Trusted Dealer Business Type Modal"
        >
          <ShieldCheckmark2Ic
            color={theme.colors.GREEN}
            width={16}
            height={16}
            aria-label="Trusted Dealer Badge"
          />
          <Styled.TrustedTypeText>
            Trusted {franchiseType}
          </Styled.TrustedTypeText>
        </Styled.TrustedDealerTypeButton>
      )}

      {FEATURE_TDP_UPDATES && dealerReviews && dealerMembershipDuration && (
        <>
          {dealerRating && (
            <SellerRatingsSummary
              membershipDuration={dealerMembershipDuration}
              ratingAverage={dealerRating}
              reviewCount={Number.parseInt(dealerReviews, 10)}
              ratingCategory={dealerRatingCategory}
              onClickRatings={onClickDealerRatings}
              onClickReviews={onClickDealerReviews}
            />
          )}
        </>
      )}

      {DISPLAY_USER_RATING && onClickReviews && onClickRatings && (
        <SellerRatingsSummary
          membershipDuration={membershipDuration}
          ratingAverage={userRating?.average}
          reviewCount={userRating?.reviewCount}
          ratingCategory={userRating?.ratingCategory}
          onClickRatings={onClickRatings}
          onClickReviews={onClickReviews}
        />
      )}

      <Styled.ActionsList>
        {renderMakeOffer && <li>{renderMakeOffer}</li>}
        {renderSendMessage && <li>{renderSendMessage}</li>}
        {renderShowPhone && <li>{renderShowPhone}</li>}
        {reservationStatus && !isCtaVariantsFlag && (
          <li>
            <ReserveCTA reservationStatus={reservationStatus} />
          </li>
        )}
      </Styled.ActionsList>
      {sellerUrl && (
        <>
          <Spacer marginBottom="M16" />
          <Styled.VisitAdsLink
            href={showroomUrl ?? sellerUrl}
            ofType="SECONDARY"
            data-tracking="view-all-ads-button"
            rel={isDealer ? undefined : 'nofollow'}
            underline
          >
            {viewAllAdsDisplayText[type]}
          </Styled.VisitAdsLink>
        </>
      )}
    </WhitePanel>
  );
};

export { SideSellerInfoPanel };
